import { AgentMessage } from '@dreamai/types';

/**
 * Utility functions for formatting tool names and messages
 * and grouping related tool messages together
 */

/**
 * Interface for grouped tool messages
 */
export interface ToolGroup {
  family: string;
  tools: AgentMessage[];
}

/**
 * Group consecutive tool messages together
 * @param messages Array of agent messages
 * @returns Array of messages with consecutive tool messages grouped together
 */
export const groupToolMessages = (
  messages: AgentMessage[],
): (AgentMessage | ToolGroup)[] => {
  const result: (AgentMessage | ToolGroup)[] = [];
  let currentGroup: ToolGroup | null = null;

  messages.forEach((message) => {
    if (message.role === 'tool') {
      // It's a tool message - add to current group or start a new one
      if (!currentGroup) {
        // Start a new group
        currentGroup = { family: 'tools', tools: [message] };
      } else {
        // Add to the current group
        currentGroup.tools.push(message);
      }
    } else {
      // Non-tool message - close any open group and add this message
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(message);
    }
  });

  // Add the last group if there is one
  if (currentGroup) {
    result.push(currentGroup);
  }

  return result;
};

/**
 * Get a formatted display for a tool based on its identifier or content
 * @param toolIdentifier The raw tool name/identifier or content containing tool identifiers
 * @param includeEmoji Whether to include emoji in the formatted output
 * @returns Formatted tool name/message with optional emoji
 */
export const formatToolDisplay = (
  toolIdentifier: string,
  includeEmoji = false,
): string => {
  // Map of tool identifiers to their formatted names with emojis
  const toolFormatMap: Record<string, { name: string; emoji: string }> = {
    // UI Tools
    createUIUpdate: { name: 'Update the interface', emoji: '🔄' },
    removeUIUpdate: { name: 'Remove interface element', emoji: '🗑️' },
    updateUIUpdate: { name: 'Update interface element', emoji: '✏️' },
    createUIView: { name: 'Create new view', emoji: '🖼️' },
    getUIViewState: { name: 'Get current interface', emoji: '🔍' },
    getThreadUIViews: { name: 'Retrieve interface', emoji: '🔍' },
    createUIFormUpdate: { name: 'Create form', emoji: '📝' },
    updateCompanyName: { name: 'Update the company name', emoji: '✏️' },
    reorderUIView: { name: 'Reorder interface', emoji: '🔄' },
    switchActiveUIView: { name: 'Switch active view', emoji: '🔄' },

    // Email Tools
    getActiveEmailAccounts: { name: 'Get active email accounts', emoji: '🔍' },
    searchEmails: { name: 'Search emails', emoji: '🔍' },
    draftFreshEmail: { name: 'Drafting email', emoji: '📝' },
    getEmailThread: { name: 'Get email thread', emoji: '🔍' },
    getEmailDetails: { name: 'Get email details', emoji: '🔍' },
    retrieveAttachment: { name: 'Retrieve attachment', emoji: '📄' },

    // WhatsApp Tools
    createWhatsAppConnectionLink: {
      name: 'Create WhatsApp connection',
      emoji: '📱',
    },
    deleteWhatsAppConnection: {
      name: 'Delete WhatsApp connection',
      emoji: '📱',
    },

    // Sales Account Tools
    authorizeAccount: { name: 'Authorize account', emoji: '🔐' },
    getAccounts: { name: 'Get accounts', emoji: '📋' },
    deleteAccount: { name: 'Delete account', emoji: '🗑️' },

    // Sales Campaign Tools
    createCampaign: { name: 'Create campaign', emoji: '📢' },
    createCampaignCanvasAsset: {
      name: 'Display campaign details',
      emoji: '📋',
    },
    deleteCampaign: { name: 'Delete campaign', emoji: '🗑️' },
    startCampaign: { name: 'Start campaign', emoji: '▶️' },
    stopCampaign: { name: 'Stop campaign', emoji: '⏹️' },
    updateCampaign: { name: 'Update campaign', emoji: '✏️' },
    getCampaigns: { name: 'Get campaigns', emoji: '📋' },

    // Sales Conversation Tools
    getApprovals: { name: 'Get approvals', emoji: '✅' },
    actionApprovals: { name: 'Action approvals', emoji: '✅' },
    getConversations: { name: 'Get conversations', emoji: '💬' },
    getEscalations: { name: 'Get escalations', emoji: '⚠️' },
    actionEscalations: { name: 'Action escalations', emoji: '⚠️' },

    // Sales Profile Tools
    getProfiles: { name: 'Get profiles', emoji: '👤' },
    createProfile: { name: 'Create profile', emoji: '👤' },
    updateBio: { name: 'Update bio', emoji: '✏️' },
    updateProfile: { name: 'Update profile', emoji: '✏️' },
    deleteProfiles: { name: 'Delete profiles', emoji: '🗑️' },

    // Sales Prospect Tools
    vaidateSourcingStrategy: {
      name: 'Validate sourcing strategy',
      emoji: '✅',
    },
    confirmSourcingStrategy: { name: 'Confirm sourcing strategy', emoji: '✅' },
    serpApiSearch: { name: 'Search with SerpAPI', emoji: '🔍' },
    coreSignalSearch: { name: 'Search with CoreSignal', emoji: '🔍' },
    getProspects: { name: 'Get prospects', emoji: '👥' },
    getTargetOrganisations: { name: 'Get target organizations', emoji: '🏢' },
    createTargetOrganisation: {
      name: 'Create target organization',
      emoji: '🏢',
    },
    updateTargetOrganisation: {
      name: 'Update target organization',
      emoji: '✏️',
    },
    setTargetOrganisations: { name: 'Set target organizations', emoji: '🏢' },
    createSourcingRun: { name: 'Create sourcing run', emoji: '🚀' },
    getLastSourcingRun: { name: 'Get last sourcing run', emoji: '📋' },
    testProspectingStrategy: { name: 'Test prospecting strategy', emoji: '🧪' },
    confirmProspectingStrategy: {
      name: 'Confirm prospecting strategy',
      emoji: '✅',
    },

    // Sales Sequence Tools
    getSequences: { name: 'Get sequences', emoji: '📋' },
    createSequence: { name: 'Create sequence', emoji: '➕' },
    updateSequence: { name: 'Update sequence', emoji: '✏️' },
    createSequenceEmail: { name: 'Create sequence email', emoji: '📧' },
    updateSequenceEmail: { name: 'Update sequence email', emoji: '✏️' },

    // Blob Storage Tools
    storeBlob: { name: 'Store data', emoji: '💾' },
    retrieveBlob: { name: 'Retrieve data', emoji: '📂' },

    // Branching Tools
    showResponseArtifact: { name: 'Presenting response', emoji: '📋' },
  };

  // First check for exact matches with the tool identifier
  const toolFormat = toolFormatMap[toolIdentifier];
  if (toolFormat) {
    return includeEmoji
      ? `${toolFormat.emoji} ${toolFormat.name}`
      : toolFormat.name;
  }

  // Then check if the content contains any of our known tool identifiers
  for (const [id, format] of Object.entries(toolFormatMap)) {
    if (toolIdentifier.includes(id)) {
      return includeEmoji ? `${format.emoji} ${format.name}` : format.name;
    }
  }

  // Default case: format the tool name if it looks like a tool identifier,
  // otherwise just return the original content
  if (toolIdentifier.match(/^[a-z]+([A-Z][a-z]*)+$/)) {
    // Looks like camelCase, so format it to sentence case
    return toolIdentifier
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .toLowerCase() // Convert to lowercase
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize only the first letter
      .trim();
  }

  // Just return the original content
  return toolIdentifier;
};
